import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { Radio } from '@/components/base/Radio'
import { ORDER_CATALOG } from '@/consts/order'
import { Team } from '@/models/team'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal'
import { SliderInput } from '@/pages/instance-list/components/SliderInput'
import { useUserStore } from '@/stores/user'
import { formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

export interface DurationOption {
  id: string
  months: number
  label: string
}

interface TeamRenewModalProps {
  open: boolean
  team: Team
  onClose: () => void
  onSuccess: () => void
}

interface DurationOptionConfig {
  id: string
  months: number
  labelKey: string
}

const DURATION_OPTIONS_CONFIG: DurationOptionConfig[] = [
  { id: '1m', months: 1, labelKey: 'duration.1month' },
  { id: '2m', months: 2, labelKey: 'duration.2months' },
  { id: '3m', months: 3, labelKey: 'duration.3months' },
  { id: '6m', months: 6, labelKey: 'duration.6months' },
  { id: '12m', months: 12, labelKey: 'duration.12months' },
  { id: 'custom', months: 0, labelKey: 'duration.custom' },
]

export function TeamRenewModal({ open, team, onClose, onSuccess }: TeamRenewModalProps) {
  const { t } = useTranslation('team')
  const userStore = useUserStore()
  const [selectedDuration, setSelectedDuration] = useState<string>('1m')
  const [customMonths, setCustomMonths] = useState(6)
  const [loading, setLoading] = useState(false)
  const [showRechargeModal, setShowRechargeModal] = useState(false)
  const [itemResID, setItemResID] = useState<string>('')
  const [itemPrice, setItemPrice] = useState<number>(0)

  // Fetch item resID and price from API
  useEffect(() => {
    if (!team.order) return

    API.getItems({
      catalog: ORDER_CATALOG.TEAM,
      page: 1,
      per_page: 10,
      from_order_id: team.order.id,
    })
      .then((res) => {
        const item = res.items.find((i) => i.id === team.order!.itemID)
        if (item) {
          setItemResID(item.resID)
          setItemPrice(item.prices[0]?.priceCent ?? 0)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch team items:', err)
      })
  }, [team.order])

  const pricePerMonth = itemPrice

  const durationOptions: DurationOption[] = useMemo(
    () =>
      DURATION_OPTIONS_CONFIG.map((config) => ({
        id: config.id,
        months: config.months,
        label: t(config.labelKey),
      })),
    [t],
  )

  const getSelectedOption = (): DurationOption => {
    if (selectedDuration === 'custom') {
      return {
        id: 'custom',
        months: customMonths,
        label: t('duration.monthLabel', { count: customMonths }),
      }
    }
    return durationOptions.find((opt) => opt.id === selectedDuration)!
  }

  const selectedOption = getSelectedOption()
  const totalPrice = pricePerMonth * selectedOption.months
  const hasEnoughBalance = userStore.balance >= totalPrice

  const handleRenew = async () => {
    if (!team.order || !itemResID) return

    setLoading(true)
    try {
      const res = await API.updateTeamOrder({
        from_order_id: team.order.id,
        res_id: itemResID,
        qty: selectedOption.months,
        modify: false,
        auto_renewal: false,
      })

      if (res.code === 0) {
        message.success(t('renew.renewSuccess'))
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to renew team:', error)
      message.error(t('renew.renewFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleRechargeSuccess = async () => {
    setShowRechargeModal(false)
    await userStore.getAccount()
  }

  if (!team.order) {
    return null
  }

  return (
    <>
      <Modal open={open} onClose={onClose} className="w-[800px] h-[600px]">
        <Modal.Header>
          <Trans ns="team" i18nKey="renew.title">
            Renew Team Plan
          </Trans>
        </Modal.Header>

        <Modal.Body className="flex-1 overflow-y-auto">
          <div className="space-y-6">
            {/* Select Duration */}
            <div>
              <h3 className="t-sb mb-4 text-primary">{t('duration.selectTitle')}</h3>
              <div className="grid grid-cols-3 gap-4">
                {durationOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => setSelectedDuration(option.id)}
                    className={cn(
                      'w-full px-4 py-3 rounded-lg border border-neutral-200 hover:border-secondary transition-colors duration-300 cursor-pointer',
                      selectedDuration === option.id && 'border-secondary bg-btn-secondary/5',
                    )}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="text-primary">
                        <div className="t-bold">{option.label}</div>
                        <div className="t-x-sb mt-1">
                          {option.id === 'custom' ? (
                            <div className="text-tertiary">{t('duration.customHint')}</div>
                          ) : (
                            formatPrice(pricePerMonth * option.months)
                          )}
                        </div>
                      </div>
                      <Radio checked={option.id === selectedDuration} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Slider for Custom Duration */}
              {selectedDuration === 'custom' && (
                <div className="mt-4 p-4 border border-gray300 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 mb-4 text-primary">
                    <span className="t-bold bg-neutral-100 px-2 rounded">
                      {customMonths}{' '}
                      <span className="text-tertiary ml-1">{t('duration.monthUnit')}</span>
                    </span>
                    <span className="t-x-sb">{formatPrice(pricePerMonth * customMonths)}</span>
                  </div>
                  <SliderInput
                    value={customMonths}
                    min={1}
                    max={12}
                    step={1}
                    unit={t('duration.monthUnit')}
                    showReset={false}
                    showInput={false}
                    onChange={setCustomMonths}
                  />
                </div>
              )}
            </div>

            {/* Balance Info */}
            {!hasEnoughBalance && (
              <div className="p-3 bg-error/10 text-error rounded-lg t-sm">
                {t('renew.insufficientBalance')}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex items-center justify-between w-full">
            <span className="h4 text-highlight">{formatPrice(totalPrice)}</span>
            <div className="flex gap-4">
              <Button type="outline" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              {hasEnoughBalance ? (
                <Button type="secondary" onClick={handleRenew} disabled={loading}>
                  {loading ? t('common.loading') : t('renew.renewButton')}
                </Button>
              ) : (
                <Button type="secondary" onClick={() => setShowRechargeModal(true)}>
                  {t('renew.rechargeButton')}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {showRechargeModal && (
        <ExchargeModal onOk={handleRechargeSuccess} onClose={() => setShowRechargeModal(false)} />
      )}
    </>
  )
}
